import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from 'react';
import { __, sprintf } from '../i18n/vsme';
import { createFileStoreApi, toFileStoreError, type ConnectorPick } from './fileStoreApi';
import type { ConnectorFile, ConnectorFilesResponse, FileConnector } from './fileStoreTypes';
import { FILE_STORE_SEARCH_DEBOUNCE_MS } from './useFileStore';

export type ConnectorFileBrowserProps = {
  /** The `accounting-hub/v1` REST base. */
  apiBase: string;
  /** A `wp_rest` nonce. */
  nonce: string;
  ownerRecordId: number;
  /** Connectors already listed by the caller; when absent the browser asks for them. */
  connectors?: FileConnector[];
  /** "Suivant": the selection, in the order it was picked. */
  onPicked: (items: ConnectorPick[]) => void;
  onCancel: () => void;
};

type Loadable<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; error: string };

/** One remote file across connectors: two connectors may reuse the same remote id. */
const pickKey = (connectionId: string, remoteFileId: string): string => `${connectionId}\u0000${remoteFileId}`;

type RemoteRowProps = {
  pick: ConnectorPick;
  selected: boolean;
  /** Shown on pinned rows, which may come from another connector. */
  origin?: string;
  onToggle: (pick: ConnectorPick) => void;
};

const RemoteRow = memo(({ pick, selected, origin, onToggle }: RemoteRowProps) => (
  <li className="connector-browser__row">
    <label className="connector-browser__name">
      <input type="checkbox" aria-label={pick.name} checked={selected} onChange={() => onToggle(pick)} />
      <span>{pick.name}</span>
    </label>
    {origin !== undefined && <span className="connector-browser__origin">{origin}</span>}
  </li>
));

RemoteRow.displayName = 'RemoteRow';

const toPick = (connectionId: string, file: ConnectorFile): ConnectorPick => ({
  connectionId,
  remote_file_id: file.remote_file_id,
  name: file.name,
});

type ProviderSidebarProps = {
  connectors: FileConnector[];
  selectedId: string;
  onSelect: (connectionId: string) => void;
};

const ProviderSidebar = ({ connectors, selectedId, onSelect }: ProviderSidebarProps) => (
  <nav className="connector-browser__providers" aria-label={__('Connecteurs')}>
    {connectors.map((connector) => {
      const selected = connector.id === selectedId;
      return (
        <button
          key={connector.id}
          type="button"
          aria-pressed={selected}
          className={selected
            ? 'connector-browser__provider connector-browser__provider--selected'
            : 'connector-browser__provider'}
          onClick={() => onSelect(connector.id)}
        >
          {connector.logo_url !== '' && <img src={connector.logo_url} alt="" />}
          {connector.name}
        </button>
      );
    })}
  </nav>
);

/**
 * "Importer via API": a sidebar of connectors and the selected one's files. Picked files are
 * pinned on top (across connectors) and handed to `onPicked` on "Suivant"; nothing is stored here.
 */
const ConnectorFileBrowser = ({
  apiBase,
  nonce,
  ownerRecordId,
  connectors: givenConnectors,
  onPicked,
  onCancel,
}: ConnectorFileBrowserProps) => {
  const api = useMemo(() => createFileStoreApi({ apiBase, nonce }), [apiBase, nonce]);
  const [fetchedConnectors, setFetchedConnectors] = useState<Loadable<FileConnector[]>>({ status: 'loading' });
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Tagged with its connection: after a switch, the previous connector's answer reads as loading.
  const [answer, setAnswer] = useState<{ connectionId: string; files: Loadable<ConnectorFilesResponse> } | null>(null);
  const [selection, setSelection] = useState<ConnectorPick[]>([]);

  useEffect(() => {
    if (givenConnectors !== undefined) {
      return undefined;
    }
    let cancelled = false;
    api.connectors(ownerRecordId).then(
      (data) => {
        if (!cancelled) {
          setFetchedConnectors({ status: 'ready', data });
        }
      },
      (error: unknown) => {
        if (!cancelled) {
          setFetchedConnectors({ status: 'error', error: toFileStoreError(error).message });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [api, ownerRecordId, givenConnectors]);

  const connectorsState = useMemo<Loadable<FileConnector[]>>(
    () => (givenConnectors === undefined ? fetchedConnectors : { status: 'ready', data: givenConnectors }),
    [givenConnectors, fetchedConnectors],
  );
  const connectors = useMemo(
    () => (connectorsState.status === 'ready' ? connectorsState.data : []),
    [connectorsState],
  );
  const current = connectors.find((connector) => connector.id === chosenId) ?? connectors[0];
  const listedId = current?.linked === true ? current.id : null;

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), FILE_STORE_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (listedId === null) {
      return undefined;
    }
    let cancelled = false;
    api.connectorFiles(ownerRecordId, listedId, { search: debouncedSearch }).then(
      (data) => {
        if (!cancelled) {
          setAnswer({ connectionId: listedId, files: { status: 'ready', data } });
        }
      },
      (error: unknown) => {
        if (!cancelled) {
          setAnswer({ connectionId: listedId, files: { status: 'error', error: toFileStoreError(error).message } });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [api, ownerRecordId, listedId, debouncedSearch]);

  const files = useMemo<Loadable<ConnectorFilesResponse>>(
    () => (answer !== null && answer.connectionId === listedId ? answer.files : { status: 'loading' }),
    [answer, listedId],
  );

  const selectedKeys = useMemo(
    () => new Set(selection.map((pick) => pickKey(pick.connectionId, pick.remote_file_id))),
    [selection],
  );
  const connectorNames = useMemo(
    () => new Map(connectors.map((connector) => [connector.id, connector.name])),
    [connectors],
  );

  const unpicked = useMemo(() => {
    if (listedId === null || files.status !== 'ready') {
      return [];
    }
    return files.data.items
      .filter((file) => !selectedKeys.has(pickKey(listedId, file.remote_file_id)))
      .map((file) => toPick(listedId, file));
  }, [files, listedId, selectedKeys]);

  const handleToggle = useCallback((pick: ConnectorPick) => {
    setSelection((picked) => {
      const key = pickKey(pick.connectionId, pick.remote_file_id);
      return picked.some((item) => pickKey(item.connectionId, item.remote_file_id) === key)
        ? picked.filter((item) => pickKey(item.connectionId, item.remote_file_id) !== key)
        : [...picked, pick];
    });
  }, []);

  const selectAllListed = () => setSelection((picked) => [...picked, ...unpicked]);

  const renderFiles = () => {
    if (files.status === 'error') {
      return <p className="connector-browser__error" role="alert">{files.error}</p>;
    }
    if (files.status === 'loading') {
      return <p className="connector-browser__loading" role="status">{__('Chargement des fichiers…')}</p>;
    }
    if (files.data.items.length === 0) {
      return <p className="connector-browser__empty">{__('Aucun fichier ne correspond.')}</p>;
    }
    return (
      <>
        <ul className="connector-browser__list">
          {unpicked.map((pick) => (
            <RemoteRow key={pick.remote_file_id} pick={pick} selected={false} onToggle={handleToggle} />
          ))}
        </ul>
        {files.data.total > files.data.items.length && (
          <p className="connector-browser__more">
            {sprintf(
              __('%1$d fichiers affichés sur %2$d. Affinez la recherche pour voir les autres.'),
              files.data.items.length,
              files.data.total,
            )}
          </p>
        )}
      </>
    );
  };

  const renderPanel = () => {
    if (connectorsState.status === 'error') {
      return <p className="connector-browser__error" role="alert">{connectorsState.error}</p>;
    }
    if (connectorsState.status === 'loading') {
      return <p className="connector-browser__loading" role="status">{__('Chargement des connecteurs…')}</p>;
    }
    if (current === undefined) {
      return <p className="connector-browser__empty">{__('Aucun connecteur disponible.')}</p>;
    }

    return (
      <>
        {current.linked && (
          <input
            type="search"
            className="connector-browser__search"
            aria-label={__('Rechercher')}
            placeholder={__('Rechercher')}
            value={search}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
          />
        )}
        {selection.length > 0 && (
          <section className="connector-browser__pinned" aria-label={__('Fichiers sélectionnés')}>
            <button type="button" className="connector-browser__bulk" onClick={() => setSelection([])}>
              {__('Tout désélectionner')}
            </button>
            <ul className="connector-browser__list">
              {selection.map((pick) => (
                <RemoteRow
                  key={pickKey(pick.connectionId, pick.remote_file_id)}
                  pick={pick}
                  selected
                  origin={connectorNames.get(pick.connectionId)}
                  onToggle={handleToggle}
                />
              ))}
            </ul>
          </section>
        )}
        {current.linked
          ? (
            <section className="connector-browser__files" aria-label={sprintf(__('Fichiers de %s'), current.name)}>
              <button
                type="button"
                className="connector-browser__bulk"
                disabled={unpicked.length === 0}
                onClick={selectAllListed}
              >
                {__('Tout sélectionner')}
              </button>
              {renderFiles()}
            </section>
          )
          : (
            <p className="connector-browser__not-linked">
              {sprintf(__("Ce dossier n'est pas relié à %s"), current.name)}
            </p>
          )}
      </>
    );
  };

  return (
    <div className="connector-browser">
      <div className="connector-browser__body">
        {connectors.length > 0 && current !== undefined && (
          <ProviderSidebar connectors={connectors} selectedId={current.id} onSelect={setChosenId} />
        )}
        <div className="connector-browser__panel">{renderPanel()}</div>
      </div>
      <div className="connector-browser__actions">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          {__('Annuler')}
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={selection.length === 0}
          onClick={() => onPicked(selection)}
        >
          {__('Suivant')}
        </button>
      </div>
    </div>
  );
};

export default ConnectorFileBrowser;
